import { takeLatest, takeEvery } from 'redux-saga';
import { put, take, call, fork, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';
import _ from 'lodash';

import { getUserId } from '../../selectors/user-selectors';
import { getCampaignPermissionTrackedAssets } from '../../utils/jsonapi-functions/campaign-permissions';
import { getTrackedAssetAnalytics } from '../../utils/tracked-assets-analytics-service';
import { getTrackedAssetList } from '../../utils/jsonapi-functions/tracked-assets';
import { getVideoListAnalytics, getVideoHistory } from '../../utils/analytics-service';
import { dyzioEngagementScore } from '../../utils/helper-functions';
import { formatYoutubeVideoAnalytics, formatInstagramAnalytics, formatFacebookAnalytics, formatTwitterAnalytics } from '../../utils/tracked-asset-helpers';
import {
  getAllYoutubeVideos,
  getAllInstagramPosts,
  getFilteredInstagramPosts,
  getAllFacebookPosts,
  getFilteredFacebookPosts,
  getAllTwitterPosts,
  getFilteredTwitterPosts,
  getUserVideos,
  getFilteredYoutubeVideos,
  getCampaignVideos,
  getSortBy,
  getFilterBy
} from '../../selectors/analytics-asset-list-selectors';

import { reportError } from '../../utils/error-handling-service';
import { YOUTUBE, INSTAGRAM, FACEBOOK, TWITTER, SNAPCHAT } from '../../utils/static-configuration';

import {
  LOAD_ANALYTICS_ASSET_LIST,
  LOAD_ANALYTICS_ASSET_LIST_ERROR,
  LOAD_ANALYTICS_ASSET_LIST_SUCCESS,
  GET_NEW_ASSET,
  SORT_ANALYTICS_ASSET_LIST,
  UPDATE_ANALYTICS_ASSET_LIST,
  FILTER_ANALYTICS_ASSET_LIST,
  ADD_TRACKED_ASSET_TO_LIST,
  GET_NEW_ASSET_ERROR,
} from './constants';

export function* loadAnalyticsAssetList() {
  try {
    const userId = yield select(getUserId);
    const sortBy = yield select(getSortBy);

    const filterByOptions = [
      { value: 'allVideos', label: 'all deliverables' },
      { value: 'myVideos', label: 'my tracked deliverables' },
      { value: 'campaignVideos', label: 'campaign deliverables' },
    ];

    const allTrackedAssetsList = [];

    // Get all campaign trackedAssets
    const allCampaignsData = yield call(getCampaignPermissionTrackedAssets, userId);
    const campaignTrackedAssets = {};
    allCampaignsData && allCampaignsData.forEach(campaignData => {
      if (campaignData.campaign.trackedAssets && campaignData.campaign.trackedAssets[0]) {
        campaignData.campaign.trackedAssets.forEach(trackedAsset => {
          _.set(campaignTrackedAssets, [trackedAsset.assetId], trackedAsset);
          allTrackedAssetsList.push(trackedAsset);
        });
      }
      filterByOptions.push({ value: campaignData.campaign.id, label: campaignData.campaign.name });
      campaignData.campaign.discussions.forEach(discussion => {
        if (discussion && discussion.trackedAssets[0]) {
          discussion.trackedAssets.forEach(trackedAsset => {
            _.set(campaignTrackedAssets, [trackedAsset.assetId], trackedAsset);
            allTrackedAssetsList.push(trackedAsset);
          });
        }
      });
    });
    const immutableCampaignAssets = fromJS(campaignTrackedAssets);

    // Get all user trackedAssets
    const userData = yield call(getTrackedAssetList, userId);
    const userTrackedAssets = {};
    userData && userData[0] && userData.forEach(trackedAsset => {
      if (!trackedAsset.campaign) {
        _.set(userTrackedAssets, [trackedAsset.assetId], trackedAsset);
        allTrackedAssetsList.push(trackedAsset);
      }
    });
    const immutableUserAssets = fromJS(userTrackedAssets);

    // Get the data for all trackedAssets
    const trackedAssetsAnalyticsList = yield call(getTrackedAssetAnalytics, allTrackedAssetsList);

    const everyTrackedAssetList = { ...userTrackedAssets, ...campaignTrackedAssets };

    const youtubeVideos = {};
    const instagramPosts = {};
    const facebookPosts = {};
    const twitterPosts = {};

    const platformOptions = {
      YOUTUBE: youtubeVideos,
      INSTAGRAM: instagramPosts,
      FACEBOOK: facebookPosts,
      TWITTER: twitterPosts,
    };

    extractAnalyticsIntoPlatformChannels(trackedAssetsAnalyticsList, everyTrackedAssetList, platformOptions);

    const immutableYoutubeVideoList = fromJS(youtubeVideos);
    const immutableInstagramPostList = fromJS(instagramPosts);
    const immutableFacebookPostList = fromJS(facebookPosts);
    const immutableTwitterPostList = fromJS(twitterPosts);

    const hasTrackedDeliverables = (immutableYoutubeVideoList.size > 0 ||
      immutableInstagramPostList.size > 0 ||
      immutableFacebookPostList.size > 0 ||
      immutableTwitterPostList.size > 0);

    yield put({
      type: LOAD_ANALYTICS_ASSET_LIST_SUCCESS,
      hasTrackedDeliverables,
      youtubeVideos: sortAssetList(immutableYoutubeVideoList, sortBy),
      instagramPosts: sortAssetList(immutableInstagramPostList, sortBy),
      facebookPosts: sortAssetList(immutableFacebookPostList, sortBy),
      twitterPosts: sortAssetList(immutableTwitterPostList, sortBy),
      filterByOptions,
      userVideos: immutableUserAssets,
      campaignVideos: immutableCampaignAssets
    });

  } catch(error) {
    reportError('LOAD_ANALYTICS_ASSET_LIST_ERROR', error.message);
    yield put({ type: LOAD_ANALYTICS_ASSET_LIST_ERROR, error });
  }
}

export const extractAnalyticsIntoPlatformChannels = (trackedAssetsAnalyticsList, everyTrackedAssetList, platformOptions) => {
  trackedAssetsAnalyticsList && trackedAssetsAnalyticsList.forEach(trackedAsset => {
    const { data } = trackedAsset.response;
    if (data) {
      let analyticsData = null;
      if (trackedAsset.platform === YOUTUBE) analyticsData = formatYoutubeVideoAnalytics(data);
      if (trackedAsset.platform === INSTAGRAM) analyticsData = formatInstagramAnalytics(data);
      if (trackedAsset.platform === FACEBOOK) analyticsData = formatFacebookAnalytics(data);
      if (trackedAsset.platform === TWITTER) analyticsData = formatTwitterAnalytics(data);

      if (analyticsData) {
        let addedInfo = analyticsData;
        if (everyTrackedAssetList[trackedAsset.assetId]) {
          addedInfo = { ...analyticsData, ...everyTrackedAssetList[trackedAsset.assetId] };
          _.set(platformOptions[trackedAsset.platform], [trackedAsset.assetId], addedInfo);
        }
      }
    }
  });
};

const lowestViews = (trackedAssetList) => { return trackedAssetList.sortBy(trackedAsset => trackedAsset.get('views')); };
const highestViews = (trackedAssetList) => { return trackedAssetList.sortBy(trackedAsset => -trackedAsset.get('views')); };
const lowestEngagement = (trackedAssetList) => { return trackedAssetList.sortBy(trackedAsset => trackedAsset.get('engagementScore')); };
const highestEngagement = (trackedAssetList) => { return trackedAssetList.sortBy(trackedAsset => -trackedAsset.get('engagementScore')); };
const oldestLiveDate = (trackedAssetList) =>
  { return trackedAssetList.sort((a, b) => {
    const aDate = new Date(a.get('publishedDate'));
    const bDate = new Date(b.get('publishedDate'));
    if (aDate < bDate) { return -1; }
    if (aDate > bDate) { return 1; }
    if (aDate === bDate)  { return 0; }
  });
};

const latestLiveDate = (trackedAssetList) =>
  { return trackedAssetList.sort((a, b) => {
    const aDate = new Date(a.get('publishedDate'));
    const bDate = new Date(b.get('publishedDate'));
    if (aDate > bDate) { return -1; }
    if (aDate < bDate) { return 1; }
    if (aDate === bDate)  { return 0; }
  });
};

function sortAssetList(videoList, sortBy) {
  const selectAssetList = {
    highestViews: highestViews,
    lowestViews: lowestViews,
    highestEngagement: highestEngagement,
    lowestEngagement: lowestEngagement,
    latestLiveDate: latestLiveDate,
    oldestLiveDate: oldestLiveDate,
  };
  return selectAssetList[sortBy](videoList);
}

export function* sortAnalyticsAssetList(actions) {
  const sortBy = actions.data.sortBy;
  const filteredYoutubeVideos = yield select(getFilteredYoutubeVideos);
  const filteredInstagramPosts = yield select(getFilteredInstagramPosts);
  const filteredFacebookPosts = yield select(getFilteredFacebookPosts);
  const filteredTwitterPosts = yield select(getFilteredTwitterPosts);
  const filterBy = yield select (getFilterBy);
  yield put({
    type: UPDATE_ANALYTICS_ASSET_LIST,
    filteredYoutubeVideos: sortAssetList(filteredYoutubeVideos, sortBy),
    filteredInstagramPosts: sortAssetList(filteredInstagramPosts, sortBy),
    filteredFacebookPosts: sortAssetList(filteredFacebookPosts, sortBy),
    filteredTwitterPosts: sortAssetList(filteredTwitterPosts, sortBy),
    sortBy,
    filterBy
  });
}

export function* getAndFormatTrackedAssetAnalytics(trackedAsset) {

  try {
    const newTrackedAssets = yield call(getTrackedAssetAnalytics, [trackedAsset]);

    const { data } = newTrackedAssets[0].response;
    if (data) {
      let analyticsData = null;
      if (trackedAsset.platform === YOUTUBE) analyticsData = formatYoutubeVideoAnalytics(data)
      if (trackedAsset.platform === INSTAGRAM) analyticsData = formatInstagramAnalytics(data)
      if (trackedAsset.platform === FACEBOOK) analyticsData = formatFacebookAnalytics(data)
      if (trackedAsset.platform === TWITTER) analyticsData = formatTwitterAnalytics(data)

      if (analyticsData) {
        const updatedTrackedAsset = { ...analyticsData, ...trackedAsset };
        if (!trackedAsset.campaign) updatedTrackedAsset.isUserAsset = true;
        return updatedTrackedAsset;
      }
      return null;
    }
  } catch (error) {
    reportError('GET_NEW_ASSET_ERROR', error.message);
    yield put({ type: GET_NEW_ASSET_ERROR, error });
  }
}

export function* addTrackedAssetToStateList(actions) {
  try {
    const { trackedAsset } = actions.data;

    const updatedTrackedAsset = yield call(getAndFormatTrackedAssetAnalytics, trackedAsset);

    if (updatedTrackedAsset) {
      let assetList = null;
      if (updatedTrackedAsset.platform === YOUTUBE) assetList = 'youtubeVideos';
      if (updatedTrackedAsset.platform === INSTAGRAM) assetList = 'instagramPosts';
      if (updatedTrackedAsset.platform === FACEBOOK) assetList = 'facebookPosts';
      if (updatedTrackedAsset.platform === TWITTER) assetList = 'twitterPosts';

      yield put({ type: ADD_TRACKED_ASSET_TO_LIST, newAsset: fromJS(updatedTrackedAsset), assetId: trackedAsset.assetId, assetList });

      const filterBy = yield select(getFilterBy);
      yield put({ type: FILTER_ANALYTICS_ASSET_LIST, data: { filterBy } });
    }
  } catch (error) {
    reportError('ADD_TRACKED_ASSET_TO_ANALYTICS_ERROR', error.message);
    yield put({ type: GET_NEW_ASSET_ERROR, error });
  }
}

export function* filterAnalyticsAssetList(actions) {
  const filterBy = actions.data.filterBy;
  const sortBy = yield select(getSortBy);
  const youtubeVideos = yield select(getAllYoutubeVideos);
  const instagramPosts = yield select(getAllInstagramPosts);
  const facebookPosts = yield select(getAllFacebookPosts);
  const twitterPosts = yield select(getAllTwitterPosts);

  let filteredYoutubeVideos = {};
  let filteredInstagramPosts = {};
  let filteredFacebookPosts = {};
  let filteredTwitterPosts = {};

  const analyticsFilteredChannelOptions = {
    YOUTUBE: filteredYoutubeVideos,
    INSTAGRAM: filteredInstagramPosts,
    FACEBOOK: filteredFacebookPosts,
    TWITTER: filteredTwitterPosts,
  };

  const analyticsChannelLists = {
    YOUTUBE: youtubeVideos,
    INSTAGRAM: instagramPosts,
    FACEBOOK: facebookPosts,
    TWITTER: twitterPosts,
  };

  const addFilteredTrackedAsset = (platform, assetId) => {
    const trackedAsset = analyticsChannelLists[platform].get(assetId);
    if (trackedAsset) _.set(analyticsFilteredChannelOptions[platform], [assetId], trackedAsset);
  };

  const showAnalyticTotals = (filterBy !== 'allVideos');

  if (filterBy === 'allVideos') {
    filteredYoutubeVideos = youtubeVideos;
    filteredInstagramPosts = instagramPosts;
    filteredFacebookPosts = facebookPosts;
    filteredTwitterPosts = twitterPosts;
  } else
  if (filterBy === 'myVideos') {
    const userVideos = yield select(getUserVideos);
    userVideos.forEach(trackedAsset => addFilteredTrackedAsset(trackedAsset.get('platform'), trackedAsset.get('assetId')));
  } else
  if (filterBy === 'campaignVideos') {
    const campaignVideos = yield select(getCampaignVideos);
    campaignVideos.forEach(trackedAsset => addFilteredTrackedAsset(trackedAsset.get('platform'), trackedAsset.get('assetId')));
  } else {
    const campaignVideos = yield select(getCampaignVideos);
    filterBy && campaignVideos.forEach(trackedAsset => {
      if (trackedAsset.getIn(['campaign', 'id']) === filterBy) {
        addFilteredTrackedAsset(trackedAsset.get('platform'), trackedAsset.get('assetId'));
      }
    });
  }
  const immutableFilteredYoutubeVideos = fromJS(filteredYoutubeVideos);
  const immutableFilteredInstagramPosts = fromJS(filteredInstagramPosts);
  const immutableFilteredFacebookPosts = fromJS(filteredFacebookPosts);
  const immutableFilteredTwitterPosts = fromJS(filteredTwitterPosts);

  let youtubeTotals = {};
  let instagramTotals = {};
  let facebookTotals = {};
  let twitterTotals = {};

  if (showAnalyticTotals) {
    youtubeTotals = extractAnalyticsTotalData(immutableFilteredYoutubeVideos, YOUTUBE);
    instagramTotals = extractAnalyticsTotalData(immutableFilteredInstagramPosts, INSTAGRAM);
    facebookTotals = extractAnalyticsTotalData(immutableFilteredFacebookPosts, FACEBOOK);
    twitterTotals = extractAnalyticsTotalData(immutableFilteredTwitterPosts, TWITTER);
  }

  yield put({
    type: UPDATE_ANALYTICS_ASSET_LIST,
    filteredYoutubeVideos: sortAssetList(immutableFilteredYoutubeVideos, sortBy),
    filteredInstagramPosts: sortAssetList(immutableFilteredInstagramPosts, sortBy),
    filteredFacebookPosts: sortAssetList(immutableFilteredFacebookPosts, sortBy),
    filteredTwitterPosts: sortAssetList(immutableFilteredTwitterPosts, sortBy),
    showAnalyticTotals,
    youtubeTotals: fromJS(youtubeTotals),
    instagramTotals: fromJS(instagramTotals),
    facebookTotals: fromJS(facebookTotals),
    twitterTotals: fromJS(twitterTotals),
    sortBy,
    filterBy
  });
}

export function extractAnalyticsTotalData(trackedAssetList, platform) {

  const totals = {
    likes: 0,
    reactions: 0,
    dislikes: 0,
    comments: 0,
    shares: 0,
    retweets: 0,
    views: 0,
    engagement: 0,
    followers: 0,
  };
  const uniqueInfluencers = [];

  const validateNumber = value => (value && !isNaN(value)) ? value : 0;
  trackedAssetList.forEach(trackedAsset => {
    totals.likes += validateNumber(trackedAsset.get('likes'));
    totals.reactions += validateNumber(trackedAsset.get('reactions'));
    totals.dislikes += validateNumber(trackedAsset.get('dislikes'));
    totals.comments += validateNumber(trackedAsset.get('comments'));
    totals.shares += validateNumber(trackedAsset.get('shares'));
    totals.retweets += validateNumber(trackedAsset.get('retweets'));
    totals.views += validateNumber(trackedAsset.get('views'));

    const platformId = trackedAsset.get('platformId');
    if (platformId) {
      const influencerExists = _.findIndex(uniqueInfluencers, id => id === platformId);
      if (influencerExists < 0) {
        uniqueInfluencers.push(platformId);
        totals.followers += validateNumber(trackedAsset.get('followers'));
      }
    }
  });
  const denominator = (platform === INSTAGRAM) ? totals.followers : totals.views;
  if (denominator) totals.engagement = dyzioEngagementScore(totals.likes, totals.dislikes, totals.comments, denominator);

  return totals;
}

export function* watchAnalyticsAssetList() {
  yield takeLatest(LOAD_ANALYTICS_ASSET_LIST, loadAnalyticsAssetList);
}

export function* watchSortAnalyticsAssetList() {
  yield takeLatest(SORT_ANALYTICS_ASSET_LIST, sortAnalyticsAssetList);
}

export function* watchFilterAnalyticsAssetList() {
  yield takeLatest(FILTER_ANALYTICS_ASSET_LIST, filterAnalyticsAssetList);
}

export function* watchAddTrackedAssetToStateList() {
  yield takeEvery(GET_NEW_ASSET, addTrackedAssetToStateList);
}

export default function* analyticsAssetListSagas() {
  yield [
    fork(watchAnalyticsAssetList),
    fork(watchSortAnalyticsAssetList),
    fork(watchAddTrackedAssetToStateList),
    fork(watchFilterAnalyticsAssetList),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/analytics-asset-list/sagas.js
 **/